import { Checkbox } from '@carbon/react';
import { SeriesSelector } from 'components/series-selector';
import { MineralType } from 'types/minerals';
import style from './chart-actions.module.scss';
import classNames from 'classnames';
import {
  PlayFilledAlt as TriangleIcon,
  CircleFilled as CircleIcon,
} from '@carbon/react/icons';
import { Toggle } from 'components/toggle/toggle';
import {
  VolumeDisplayModeRadio,
  type VolumeDisplayMode,
} from './volume-display-mode-radio';

export type { VolumeDisplayMode };

interface ChartActionsProps {
  selectedLegend: Array<MineralType | 'GOR' | 'WOR' | 'WGR' | 'CGR'>;
  handleLegendClick: (legend: MineralType) => void;
  showMarkers: {
    segmentation: boolean;
    outlier: boolean;
  };
  setShowMarkers: (showMarkers: {
    segmentation: boolean;
    outlier: boolean;
  }) => void;
  enableMarkers: boolean;
  enableTypeCurve: boolean;
  showTypeCurve: boolean;
  setShowTypeCurve: (show: boolean) => void;
  volumeDisplayMode: VolumeDisplayMode;
  onVolumeDisplayModeChange: (mode: VolumeDisplayMode) => void;
  /**
   * The series the legend lists. Defaults to every stream and ratio, for a
   * chart grid that includes a ratio chart. A section with only a volumes
   * chart passes the three streams.
   */
  streams?: string[];
  /**
   * Prefix for the checkbox ids. A page showing two of these at once has to
   * give them different prefixes, or both sets of checkboxes share ids and a
   * label toggles whichever one the document holds first.
   */
  testId?: string;
}

export const ChartActions = ({
  selectedLegend,
  handleLegendClick,
  showMarkers,
  setShowMarkers,
  enableMarkers,
  showTypeCurve,
  setShowTypeCurve,
  enableTypeCurve,
  volumeDisplayMode,
  onVolumeDisplayModeChange,
  streams = ['oil', 'gas', 'water', 'GOR', 'WOR', 'WGR', 'CGR'],
  testId = 'decline-curve-legend-container',
}: ChartActionsProps) => {
  return (
    <div className={style.chartActionsContainer}>
      <form
        className={classNames(style.markersControlForm, {
          [style.hidden]: !enableMarkers,
        })}
      >
        <Checkbox
          id={`${testId}-segmentation-markers-checkbox`}
          className={classNames({
            [style.activeCheckbox]: !showMarkers.segmentation,
          })}
          labelText={
            <div className={style.checkboxlabelText}>
              <TriangleIcon className={style.checkboxLabelIcon} />
              <span>Production Stream Segment Start</span>
            </div>
          }
          checked={showMarkers.segmentation}
          onChange={(_evt: any, { checked }: { checked: boolean }) =>
            setShowMarkers({ ...showMarkers, segmentation: checked })
          }
        />
        <Checkbox
          id={`${testId}-outlier-markers-checkbox`}
          className={classNames({
            [style.activeCheckbox]: !showMarkers.outlier,
          })}
          labelText={
            <div className={style.checkboxlabelText}>
              <CircleIcon
                className={
                  showMarkers.outlier
                    ? style.outlierLegendMarkerActive
                    : style.outlierLegendMarkerInactive
                }
              />
              <span>Production Stream Outlier</span>
            </div>
          }
          checked={showMarkers.outlier}
          onChange={(_evt: any, { checked }: { checked: boolean }) =>
            setShowMarkers({ ...showMarkers, outlier: checked })
          }
        />
      </form>
      <SeriesSelector
        allItems={streams}
        selectedItems={selectedLegend}
        onSelectionChange={handleLegendClick as any}
        testId={testId}
      />
      <div className={style.typeCurveAndVolumeDisplayColumn}>
        <div
          className={classNames(style.typeCurveToggleContainer, {
            [style.hidden]: !enableTypeCurve,
          })}
        >
          <Toggle
            isActive={showTypeCurve}
            handleToggle={() => setShowTypeCurve(!showTypeCurve)}
            label="Display Type Curve"
          />
        </div>
        <VolumeDisplayModeRadio
          value={volumeDisplayMode}
          onChange={onVolumeDisplayModeChange}
          idPrefix={testId}
        />
      </div>
    </div>
  );
};
